import { drawFrame, mapToPlot } from './canvasUtils';

function hhStep(state, params, dt) {
    const { v, m, h, n } = state;
    const alphaM = (0.1 * (v + 40)) / (1 - Math.exp(-(v + 40) / 10) + 1e-9);
    const betaM = 4 * Math.exp(-(v + 65) / 18);
    const alphaH = 0.07 * Math.exp(-(v + 65) / 20);
    const betaH = 1 / (1 + Math.exp(-(v + 35) / 10));
    const alphaN = (0.01 * (v + 55)) / (1 - Math.exp(-(v + 55) / 10) + 1e-9);
    const betaN = 0.125 * Math.exp(-(v + 65) / 80);

    const dm = alphaM * (1 - m) - betaM * m;
    const dh = alphaH * (1 - h) - betaH * h;
    const dn = alphaN * (1 - n) - betaN * n;

    const gNa = params.gNa * (m ** 3) * h;
    const gK = params.gK * (n ** 4);
    const iNa = gNa * (v - 50);
    const iK = gK * (v + 77);
    const iL = params.gL * (v + 54.4);
    const dv = (params.current - iNa - iK - iL) / 1.0;

    state.v = v + dv * dt;
    state.m = Math.max(0, Math.min(1, m + dm * dt));
    state.h = Math.max(0, Math.min(1, h + dh * dt));
    state.n = Math.max(0, Math.min(1, n + dn * dt));
}

function drawVoltageTrace(ctx, w, h, state, opts) {
    const area = drawFrame(ctx, w, h, {
        xLabel: 't (time)',
        yLabel: 'V_m (mV)',
        title: opts.title,
        t: state.t,
        tUnit: 'ms',
    });

    const history = state.history;
    if (history.length < 2) return;

    const tMin = history[0].t;
    const tMax = state.t || tMin + 1;
    const vMin = -90;
    const vMax = 50;

    ctx.strokeStyle = opts.color || '#4fc3f7';
    ctx.lineWidth = 2;
    ctx.beginPath();
    history.forEach((pt, i) => {
        const mapped = mapToPlot(area, pt.t, pt.v, tMin, tMax, vMin, vMax);
        if (i === 0) ctx.moveTo(mapped.x, mapped.y);
        else ctx.lineTo(mapped.x, mapped.y);
    });
    ctx.stroke();

    if (opts.spikeCount !== undefined) {
        ctx.fillStyle = '#fff';
        ctx.font = '12px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(`spikes = ${opts.spikeCount}`, area.left, area.bottom + 28);
    }
}

export const hhSingleSpike = {
    init: () => ({ v: -65, m: 0.05, h: 0.6, n: 0.32, t: 0, history: [{ t: 0, v: -65 }] }),
    step: (ctx, w, h, state, params) => {
        hhStep(state, params, 0.05);
        state.t += 0.05;
        state.history.push({ t: state.t, v: state.v });
        if (state.history.length > 600) state.history.shift();

        drawVoltageTrace(ctx, w, h, state, { title: 'Single action potential', color: '#4fc3f7' });
    },
};

export const hhSpikeTrain = {
    init: () => ({
        v: -65, m: 0.05, h: 0.6, n: 0.32, t: 0,
        history: [{ t: 0, v: -65 }],
        spikeCount: 0,
        wasHigh: false,
    }),
    step: (ctx, w, h, state, params) => {
        for (let i = 0; i < 2; i++) {
            hhStep(state, params, 0.05);
            state.t += 0.05;
        }
        state.history.push({ t: state.t, v: state.v });
        if (state.history.length > 600) state.history.shift();

        const isHigh = state.v > 0;
        if (isHigh && !state.wasHigh) state.spikeCount += 1;
        state.wasHigh = isHigh;

        drawVoltageTrace(ctx, w, h, state, {
            title: 'Spike train (repetitive firing)',
            color: '#2cbb5d',
            spikeCount: state.spikeCount,
        });
    },
};

export const hhStochastic = {
    init: () => ({ v: -65, m: 0.05, h: 0.6, n: 0.32, t: 0, history: [{ t: 0, v: -65 }] }),
    step: (ctx, w, h, state, params) => {
        const noisyParams = { ...params, current: params.current + params.noise * (Math.random() - 0.5) };
        hhStep(state, noisyParams, 0.05);
        state.t += 0.05;
        state.history.push({ t: state.t, v: state.v });
        if (state.history.length > 600) state.history.shift();

        drawVoltageTrace(ctx, w, h, state, { title: 'Stochastic channel noise', color: '#f9a602' });
    },
};
